import { useEffect, useMemo, useState } from 'react';
import { CheckmarkCircle24Filled } from '@fluentui/react-icons';
import {
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useOrderDraft } from '../state/OrderDraftProvider';
import { useCateringService } from '../app/CateringProvider';
import { toOrderDraft } from '../state/orderDraft';
import type { OrderQuote } from '../types/catering';
import type { MeetingInfo } from '../types/meeting';
import { OrderSummaryCard, StickyActionFooter } from '../components';
import { ORDER_PATHS, useRouter } from '../router';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
    gap: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: '5.5rem',
  },
  iconContainer: {
    width: '4.5rem',
    height: '4.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  icon: {
    color: '#16A34A',
    width: '3.5rem',
    height: '3.5rem',
  },
  message: {
    width: '100%',
    textAlign: 'center',
  },
});

export type OrderConfirmationPageProps = {
  meetingTitle?: MeetingInfo['subject'];
};

export default function OrderConfirmationPage({ meetingTitle }: OrderConfirmationPageProps) {
  const styles = useStyles();
  const service = useCateringService();
  const { state, dispatch } = useOrderDraft();
  const { navigate } = useRouter();
  const draft = useMemo(() => toOrderDraft(state), [state]);
  const [quote, setQuote] = useState<OrderQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);

  useEffect(() => {
    if (!draft) return;

    let isCurrent = true;
    service
      .quoteOrder(draft)
      .then((loadedQuote) => {
        if (isCurrent) setQuote(loadedQuote);
      })
      .catch(() => {
        if (isCurrent) setQuoteError('We could not load the order summary.');
      });

    return () => {
      isCurrent = false;
    };
  }, [draft, service]);

  return (
    <main className={styles.root}>
      <div className={styles.iconContainer}>
        <CheckmarkCircle24Filled className={styles.icon} aria-hidden="true" />
      </div>
      <Text as="h1" size={500} weight="semibold">
        Order submitted
      </Text>
      <Text className={styles.message}>Your catering order has been submitted successfully.</Text>
      {quote ? (
        <OrderSummaryCard
          headcount={draft?.setup.headcount ?? 0}
          quote={quote}
          title={meetingTitle?.trim() || 'Catering'}
        />
      ) : quoteError ? (
        <MessageBar intent="error">
          <MessageBarBody>{quoteError}</MessageBarBody>
        </MessageBar>
      ) : (
        <Spinner size="small" label="Loading order summary" />
      )}
      <StickyActionFooter
        label="Place another order"
        onClick={() => {
          dispatch({ type: 'resetOrderDraft' });
          navigate(ORDER_PATHS.setup);
        }}
      />
    </main>
  );
}
